import React, { useState } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { useResolveFraudFlag } from '../../../hooks/useApi';
import { FraudFlag } from '../../../types';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import { useTimezone, formatInTimezone } from '@kioskgaming/ui';

interface ResolveFraudFlagModalProps {
  flag: FraudFlag;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ResolveFraudFlagModal: React.FC<ResolveFraudFlagModalProps> = ({
  flag,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { timezone } = useTimezone();
  const [notes, setNotes] = useState('');
  const resolveFlag = useResolveFraudFlag();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await resolveFlag.mutateAsync({
        flagId: flag.id,
        data: { notes: notes.trim() || undefined },
      });
      onSuccess();
    } catch (error) {
      // Error is handled by the hook
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Resolve Fraud Flag</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Flag Information */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Transaction ID</p>
                <p className="font-mono text-sm font-medium">{flag.transactionId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">User ID</p>
                <p className="font-mono text-sm font-medium">{flag.userId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Flag Type</p>
                <p className="text-sm font-medium">{flag.flagType}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Severity</p>
                <p className="text-sm font-medium">{flag.severity}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Created At</p>
                <p className="text-sm font-medium">
                  {formatInTimezone(flag.createdAt, timezone)}
                </p>
              </div>
            </div>

            {/* Flag Data */}
            {flag.flagData && Object.keys(flag.flagData).length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Flag Data</p>
                <pre className="bg-white p-3 rounded border text-xs overflow-x-auto">
                  {JSON.stringify(flag.flagData, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* Resolution Notes */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Resolution Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter notes about why this flag is being resolved..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={resolveFlag.isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={resolveFlag.isLoading}
              >
                {resolveFlag.isLoading ? (
                  <>
                    <LoadingSpinner className="h-4 w-4 mr-2" />
                    Resolving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Resolve Flag
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResolveFraudFlagModal;

